import { useEffect, useState } from 'react'
import { api } from '../../../api'
import DisposEditor from './DisposEditor'
import FactureComTournee from './FactureComTournee'
import VisuelProduit from './VisuelProduit'
import { ORANGE, TEAL, VERT, eur, fmtJour, fmtInstant } from './paniersProd'
import { avecAlpha } from '../../../lib/couleurs'

/**
 * Ecrans d'etape de la timeline tournee (proto v7.3) : Mes dispos (SAISIE
 * palier 2 tant que les commandes sont ouvertes, lecture figee ensuite) /
 * La demande / Ma preparation / Livraison / Mon argent. La validation de
 * preparation (P4) et les ajustements (P3) viennent aux paliers suivants.
 */

const Label = ({ children }) => (
  <p className="text-[11px] font-bold uppercase mb-2 mx-0.5" style={{ letterSpacing: 2, color: TEAL }}>{children}</p>
)

const Vide = ({ icone, texte }) => (
  <div className="glass-surface rounded-3xl p-6 text-center">
    <span className="text-4xl block mb-2">{icone}</span>
    <p className="text-[13px] text-ink/65">{texte}</p>
  </div>
)

/** Hook local : ma liste de preparation (partagee par dispos/demande/preparation). */
function usePreparation(tourId) {
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  useEffect(() => {
    if (!tourId) return
    setLoading(true)
    api.producer.getTourPreparation(tourId)
      .then(setData)
      .catch(() => setData(null))
      .finally(() => setLoading(false))
  }, [tourId])
  return { data, loading }
}

/** Etape 1 — Mes dispos : SAISIE tant que les commandes sont ouvertes (P2),
 * lecture figee apres la cloture. */
export function EtapeDispos({ tourId, t, onToast, onChanged }) {
  const editable = !!t && (t.statut === 'DISPONIBILITES' || t.statut === 'COMMANDES')
  const { data, loading } = usePreparation(editable ? null : tourId)
  if (editable) return <DisposEditor tourId={tourId} onToast={onToast} onChanged={onChanged} />
  if (loading) return <div className="h-40 rounded-3xl animate-pulse bg-ink/5" />
  const lignes = data?.lignes || []
  return (
    <>
      <Label>Mes disponibilités</Label>
      {lignes.length === 0 ? (
        <Vide icone="📋" texte="Aucun produit proposé sur cette tournée." />
      ) : (
        <div className="flex flex-col gap-2">
          {lignes.map((l) => (
            <div key={l.tourOfferId} className="glass-surface rounded-[18px] flex items-center gap-3" style={{ padding: '13px 15px' }}>
              <VisuelProduit photo={l.photo} emoji={l.emoji} taille={44} />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-bold" style={{ color: VERT }}>{l.produit}</p>
                <p className="text-[11px] text-ink/50">{eur(l.prix)} / {l.unite}</p>
              </div>
              <p className="font-serif text-[15px]" style={{ color: VERT }}>{l.dispo} {l.unite}</p>
            </div>
          ))}
        </div>
      )}
      <p className="text-[10.5px] leading-relaxed mt-2.5 mx-0.5 text-ink/45">
        🦊 Les disponibilités sont figées depuis la clôture des commandes.
      </p>
    </>
  )
}

/** Etape 2 — La demande en quasi temps reel (reserve vs dispo). */
export function EtapeDemande({ tourId }) {
  const { data, loading } = usePreparation(tourId)
  if (loading) return <div className="h-40 rounded-3xl animate-pulse bg-ink/5" />
  const lignes = data?.lignes || []
  if (!lignes.length) return <Vide icone="📈" texte="Aucun produit sur cette tournée." />
  return (
    <>
      <Label>La demande, en direct</Label>
      <div className="flex flex-col gap-2">
        {lignes.map((l) => {
          const ratio = l.dispo > 0 ? Math.min(1, l.vendu / l.dispo) : 0
          const complet = ratio >= 1
          return (
            <div key={l.tourOfferId} className="glass-surface rounded-[18px]" style={{ padding: '13px 15px' }}>
              <div className="flex items-center gap-3">
                <VisuelProduit photo={l.photo} emoji={l.emoji} taille={38} />
                <p className="text-sm font-bold flex-1 min-w-0 truncate" style={{ color: VERT }}>{l.produit}</p>
                <p className="text-[12px] font-bold flex-shrink-0" style={{ color: complet ? '#0a8a5f' : VERT }}>
                  {l.vendu} / {l.dispo} {l.unite}
                </p>
              </div>
              <div className="rounded-full mt-2 overflow-hidden" style={{ height: 7, background: 'rgba(7,62,61,0.08)' }}>
                <div className="h-full rounded-full" style={{ width: `${ratio * 100}%`, background: complet ? '#0a8a5f' : `linear-gradient(90deg, ${TEAL}, ${ORANGE})` }} />
              </div>
              {complet && <p className="text-[10.5px] font-bold mt-1.5" style={{ color: '#0a8a5f' }}>Tout est réservé 🎉</p>}
            </div>
          )
        })}
      </div>
    </>
  )
}

/** Etape 3 — Ma liste de preparation (+ ecart apres collecte). Validation = P4. */
export function EtapePreparation({ tourId }) {
  const { data, loading } = usePreparation(tourId)
  if (loading) return <div className="h-40 rounded-3xl animate-pulse bg-ink/5" />
  const lignes = (data?.lignes || []).filter((l) => l.vendu > 0)
  return (
    <>
      {data?.tour?.dateCollecte && (
        <div className="glass-dark rounded-[22px] mb-3.5 flex items-center gap-3 relative overflow-hidden" style={{ padding: '15px 16px', color: 'rgb(var(--c-creme))' }}>
          <span className="text-[30px]">🧺</span>
          <div>
            <p className="text-[13.5px] font-bold">Collecte {fmtJour(data.tour.dateCollecte)}</p>
            <p className="text-[11px]" style={{ color: 'rgba(247,240,234,0.75)' }}>Manrina passe chez vous — préparez les quantités vendues.</p>
          </div>
        </div>
      )}
      <Label>À préparer ({lignes.length})</Label>
      {lignes.length === 0 ? (
        <Vide icone="🧺" texte="Rien à préparer — aucune vente sur cette tournée." />
      ) : (
        <div className="flex flex-col gap-2">
          {lignes.map((l) => {
            const ecart = l.collecte != null && Number(l.collecte) !== Number(l.vendu)
            return (
              <div key={l.tourOfferId} className="glass-surface rounded-[18px]" style={{ padding: '13px 15px', borderLeft: ecart ? `4px solid ${ORANGE}` : '4px solid transparent' }}>
                <div className="flex items-center gap-3">
                  <VisuelProduit photo={l.photo} emoji={l.emoji} taille={38} />
                  <p className="text-sm font-bold flex-1 min-w-0 truncate" style={{ color: VERT }}>{l.produit}</p>
                  <p className="font-serif text-[15px] flex-shrink-0" style={{ color: VERT }}>{l.vendu} {l.unite}</p>
                </div>
                {l.collecte != null && (
                  <p className="text-[11px] mt-1" style={{ color: ecart ? ORANGE : '#0a8a5f' }}>
                    {ecart ? `⚠️ Collecté ${l.collecte} ${l.unite} (écart ${Math.round((l.collecte - l.vendu) * 100) / 100})` : `✓ Collecté ${l.collecte} ${l.unite}`}
                  </p>
                )}
              </div>
            )
          })}
        </div>
      )}
      <p className="text-[10.5px] leading-relaxed mt-2.5 mx-0.5 text-ink/45">
        La validation « c'est prêt » (qui prévient le récupérateur) arrive au palier 4.
      </p>
    </>
  )
}

/** Etape 4 — C'est parti (suivi passif). */
export function EtapeLivraison({ t }) {
  return (
    <div className="glass-dark rounded-[28px] p-7 text-center relative overflow-hidden">
      <div className="absolute -top-10 -right-10 w-40 h-40 rounded-full" style={{ background: 'radial-gradient(circle, rgba(20,184,166,0.4), transparent 70%)' }} />
      <div className="text-6xl relative">🚚</div>
      <p className="font-serif text-2xl mt-2 relative text-creme">C'est parti</p>
      <p className="text-sm mt-1.5 relative" style={{ color: 'rgba(247,240,234,0.75)' }}>
        Manrina collecte et livre vos clients — vous n'avez rien à faire.
        {t?.dateLivraison ? ` Livraison ${fmtJour(t.dateLivraison)}.` : ''}
      </p>
    </div>
  )
}

/** Etape 5 — Mon argent (sequestre v0.81 : previsionnel → verse / credite). */
export function EtapeArgent({ tourId, onToast }) {
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [pieceVue, setPieceVue] = useState(false)
  useEffect(() => {
    if (!tourId) return
    setLoading(true)
    api.producer.getTourArgent(tourId)
      .then(setData)
      .catch(() => setData(null))
      .finally(() => setLoading(false))
  }, [tourId])
  if (loading) return <div className="h-40 rounded-3xl animate-pulse bg-ink/5" />
  if (!data) return <Vide icone="💶" texte="Suivi indisponible pour le moment." />

  const { totaux, commandes, prochaineFenetreFin, versementMode, piece } = data
  const enSequestre = (totaux?.netPrevisionnel ?? 0) > 0
  return (
    <>
      {/* Net en heros — previsionnel tant que des fenetres courent */}
      <div className="glass-dark rounded-[26px] relative overflow-hidden mb-3" style={{ padding: '20px 20px 18px' }}>
        <div className="absolute -top-10 -right-10 w-[150px] h-[150px] rounded-full pointer-events-none" style={{ background: 'radial-gradient(circle, rgba(20,184,166,0.45), transparent 70%)' }} />
        <p className="relative text-[10.5px] font-bold uppercase" style={{ letterSpacing: 2, color: 'rgba(247,240,234,0.65)' }}>
          {enSequestre ? 'Versement prévisionnel' : 'Net de la tournée'}
        </p>
        <p className="relative font-serif mt-0.5" style={{ fontSize: 38, lineHeight: 1.05, color: 'rgb(var(--c-creme))' }}>
          {eur((totaux?.netPrevisionnel ?? 0) + (totaux?.netReparti ?? 0))}
        </p>
        <p className="relative text-[11.5px] mt-1.5 leading-snug" style={{ color: 'rgba(247,240,234,0.8)' }}>
          {enSequestre
            ? `En séquestre${prochaineFenetreFin ? ` — fenêtre d'ajustement jusqu'au ${fmtInstant(prochaineFenetreFin)}` : ''}. Le montant final part après la fenêtre.`
            : versementMode === 'WALLET' ? 'Crédité sur votre wallet — utilisable dans Lyannaj.' : 'Versé sur votre compte via Stripe.'}
        </p>
      </div>

      <Label>Par commande ({commandes.length})</Label>
      <div className="flex flex-col gap-2 mb-3.5">
        {commandes.map((c) => (
          <div key={c.visibleId} className="glass-surface rounded-[16px] flex items-center gap-3" style={{ padding: '11px 14px' }}>
            <div className="flex-1 min-w-0">
              <p className="text-[12.5px] font-bold" style={{ color: VERT }}>{c.visibleId}</p>
              <p className="text-[10.5px] text-ink/50">
                {c.repartitionAt
                  ? `Répartie le ${fmtInstant(c.repartitionAt)}`
                  : c.fenetreFinAt ? `Fenêtre jusqu'au ${fmtInstant(c.fenetreFinAt)}` : 'En attente de livraison'}
              </p>
            </div>
            <p className="font-serif text-[14px]" style={{ color: c.repartitionAt ? '#0a8a5f' : VERT }}>{eur(c.net)}</p>
          </div>
        ))}
        {commandes.length === 0 && <Vide icone="💶" texte="Aucune vente en ligne sur cette tournée." />}
      </div>

      {piece && (
        <button
          onClick={() => setPieceVue(true)}
          className="w-full rounded-[14px] text-[13px] font-extrabold"
          style={{ padding: 12, border: `1px solid ${avecAlpha(ORANGE, 0.33)}`, background: 'rgba(244,137,83,0.08)', color: ORANGE }}
        >
          🧾 Ma pièce {piece.numero}
        </button>
      )}
      {pieceVue && piece && (
        <FactureComTournee piece={piece} versementMode={versementMode} enSequestre={enSequestre} onClose={() => setPieceVue(false)} onToast={onToast} />
      )}
    </>
  )
}
